import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { tokenStore } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';

/**
 * Bootstrap page that runs when admin opens a new tab via "Login as Merchant".
 *
 * URL: /impersonate?app=merchant&token=<JWT>&email=<m>&companyName=<m>&id=<n>&next=<path>
 *
 * 1. Wipes any existing tokens / persisted auth state from this origin's storage
 *    so we don't inherit the admin session that was opened the new tab.
 * 2. Stores the freshly minted merchant access token.
 * 3. Sets useAuth state so MerchantLayout sees a logged-in merchant.
 * 4. Strips the query string and replaces the URL with `next` (default /wallet).
 *
 * `next` exists for the Shopify App Store hand-off: those accounts are not allowed to be shown a
 * payment surface at all, so landing them on the wallet — this page's original and still-default
 * destination for "Login as Merchant" — would put the one forbidden page in front of them first.
 */
export function ImpersonateBootstrap() {
  const nav = useNavigate();
  const setUser = useAuth((s) => s.setUser);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (!token) {
      nav('/login', { replace: true });
      return;
    }
    const id = Number(params.get('id') || '0');
    const email = params.get('email') || '';
    const companyName = params.get('companyName') || '';

    // 1. Clear all previous auth state (admin token + zustand persist + any cached query data)
    try {
      localStorage.removeItem('courier-access-token');
      localStorage.removeItem('courier-auth');
    } catch {}

    // 2. Store the new merchant token
    tokenStore.set(token);

    // 3. Set zustand auth state as merchant
    setUser({
      id,
      kind: 'merchant',
      email,
      companyName,
    });

    // 4. Tag this session as impersonated (cosmetic — shown in the topbar banner)
    try { sessionStorage.setItem('impersonated', '1'); } catch {}

    // Replace URL so the token doesn't linger in the address bar. Only same-origin paths are
    // honoured — `next` arrives in a URL, so an absolute one would turn this into an open redirect.
    const next = params.get('next') || '';
    const dest = next.startsWith('/') && !next.startsWith('//') ? next : '/wallet';
    nav(dest, { replace: true });
  }, [nav, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-3" />
        <div className="text-slate-700 font-medium">Logging in as merchant…</div>
        <div className="text-slate-400 text-sm mt-1">Resetting session and redirecting</div>
      </div>
    </div>
  );
}
